import React, { useEffect, useRef } from 'react';
import { black_icons } from '../../icons';

function ImageUploader({ show, onHide, canvasRef, selectedTool, canvasState }) {

    const fileInputRef = useRef(null);
    const uploadAreaRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (show && !event.target.closest('.mdl-dia')) {
                onHide();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);

        };
    }, [show, onHide]);

    const getUniqueFileName = (originalName, newSrc) => {
        const existingFiles = [];
        if (canvasState) {
            if (canvasState.images && Array.isArray(canvasState.images)) {
                canvasState.images.forEach(img => {
                    if (img.fileName) {
                        existingFiles.push({ fileName: img.fileName, src: img.src });
                    }
                });
            }
            if (canvasState.paraventLogo && canvasState.paraventLogo.fileName) {
                existingFiles.push({
                    fileName: canvasState.paraventLogo.fileName,
                    src: canvasState.paraventLogo.src
                });
            }
        }

        const sameContentMatch = existingFiles.find(
            f => f.fileName === originalName && f.src === newSrc
        );
        if (sameContentMatch) {
            return originalName;
        }

        const nameCollision = existingFiles.some(f => f.fileName === originalName);
        if (!nameCollision) {
            return originalName;
        }

        const dotIndex = originalName.lastIndexOf('.');
        let baseName = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName;
        const extension = dotIndex !== -1 ? originalName.slice(dotIndex) : '';

        const match = baseName.match(/^(.*)_(\d+)$/);
        if (match) {
            baseName = match[1];
        }

        let counter = 1;
        let newName = `${baseName}_${counter}${extension}`;
        let collision = true;
        while (collision) {
            collision = false;
            for (let i = 0; i < existingFiles.length; i++) {
                const f = existingFiles[i];
                if (f.fileName === newName && f.src !== newSrc) {
                    collision = true;
                    break;
                }
            }
            if (collision) {
                counter++;
                newName = `${baseName}_${counter}${extension}`;
            }
        }
        return newName;
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
        if (file && (file.type.toLowerCase().includes('svg') || file.name.toLowerCase().endsWith('.svg'))) {
                const blob = new Blob([file], { type: 'image/svg+xml' });
                const reader = new FileReader();
                reader.onload = (e) => {
                    const uniqueFileName = getUniqueFileName(file.name, e.target.result);
                    const data = { src: e.target.result, fileName: uniqueFileName };
                    if (selectedTool === "Graphic") {
                        canvasRef.current.addImage(data);
                    } else {
                        canvasRef.current.addLogo(data);
                    }
                    onHide();
                };
                reader.readAsDataURL(blob);
                return;
            }

            if (file.type.match(/^image\/(jpg|jpeg|gif|png|webp)$/i)) {
                let processedFile = file;
                const MAX_SIZE_MB = 1;

                if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                    try {
                        processedFile = await resizeImageIfNeeded(file, MAX_SIZE_MB);
                    } catch (e) {
                        console.error("Error resizing image", e);
                    }
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    const uniqueFileName = getUniqueFileName(file.name, e.target.result);
                    if (selectedTool === "Graphic") {
                        canvasRef.current.addImage({ src: e.target.result, fileName: uniqueFileName });
                    } else {
                        canvasRef.current.addLogo({ src: e.target.result, fileName: uniqueFileName });
                    }
                };
                reader.readAsDataURL(processedFile);
                onHide();
            }
        }
        event.target.value = '';

    };

    const resizeImageIfNeeded = (file, maxSizeMB) => {
        return new Promise((resolve, reject) => {
            const img = document.createElement('img');
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                const targetSize = maxSizeMB * 1024 * 1024;
                const scaleFactor = Math.sqrt(targetSize / file.size);

                let width = Math.floor(img.width * scaleFactor);
                let height = Math.floor(img.height * scaleFactor);

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                const mimeType = (file.type === 'image/png' || file.type === 'image/webp') ? file.type : 'image/jpeg';
                canvas.toBlob((blob) => {
                    resolve(blob);
                }, mimeType, 0.9);
            };
            img.onerror = reject;
        });
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        uploadAreaRef.current.classList.add('drag-over');
    }

    const handleDragLeave = () => {
        uploadAreaRef.current.classList.remove('drag-over');
    }

    const handleDrop = (event) => {
        event.preventDefault();
        uploadAreaRef.current.classList.remove('drag-over');
        const file = event.dataTransfer.files[0];
        if (file && (file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.svg'))) {
            handleImageUpload({ target: { files: [file] } });
        }
    }


    return (
        <div className="mdl" style={{ display: show ? 'flex' : 'none' }}>
            <input type="file" accept="image/*,.svg" onChange={handleImageUpload} ref={fileInputRef} style={{ display: 'none' }} />
            <div className="mdl-dia mdl-dia-upload">
                <div
                    className="upload-area"
                    ref={uploadAreaRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}>
                    <button className="close-button upload-close-btn" onClick={onHide}><black_icons.closeIcon /></button>
                    <div className='mdl-tit text-gray-900'>DRAG YOUR IMAGE HERE</div>
                    <button className="upload-button" onClick={() => fileInputRef.current.click()}>OR CLICK HERE</button>
                    <p className='secondary-text'>JPG / JPEG / GIF / PNG / WEBP / SVG</p>
                    <p className='secondary-text' style={{ fontSize: '0.7rem', marginTop: '5px' }}>Files larger than 1MB will be automatically optimized</p>
                </div>
            </div>
        </div>
    );
}

export default ImageUploader;